import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useTranslation } from "react-i18next";

import useFansUnitedSDK from "../../hooks/useFansUnitedSDK";
import { useToast } from "../../hooks/useToast";

import Loader from "../../common/Loader";
import Toast from "../../common/Toast";
import Dropdown from "./components/Dropdown";

import {
	getMyOverallRanking,
	getMyRankingRequest,
	getMyWeeklyRanking,
	getMyWeeklyRankingRequest,
	getOverallRanking,
	getRankingRequest,
} from "../../redux/ranking";
import { getUser } from "../../redux/user";

import { TEMPLATE_ID } from "../../api/constants";
import { printProfileNickname } from "../../Utils";
import avatarIcon from "../../assets/avatar.png";
import noMembersImage from "../../assets/no-members.png";
import styles from "./RankingFragment.module.css";

const RankingFragment = () => {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(false);
	const [toastMessage, setToastMessage] = useState(null);
	const [rankingType, setRankingType] = useState("season"); // "season" or "weekly"
	const [selectedRound, setSelectedRound] = useState(null);
	const [displayedCount, setDisplayedCount] = useState(20); // Number of users to display
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isLoadingWeeklyRanking, setIsLoadingWeeklyRanking] = useState(false);

	const overallRankingList = useSelector(getOverallRanking);
	const roundRankingList = useSelector((state) => state.ranking?.roundRanking) || [];
	const myRankingList = useSelector(getMyOverallRanking);
	const myWeeklyRankingList = useSelector(getMyWeeklyRanking);
	const user = useSelector(getUser);
	const groups = useSelector((state) => state.predictor?.groups) || [];

	const fansUnitedSDK = useFansUnitedSDK();
	const showToast = useToast(() => setToastMessage(null), toastMessage);

	const dispatch = useDispatch();

	const getRoundRankingData = useCallback(
		(groupId) => {
			setIsLoading(true);
			// Import and call the function dynamically
			import("../../redux/ranking")
				.then(({ getRoundRankingRequest }) => {
					dispatch(getRoundRankingRequest(fansUnitedSDK, groupId))
						.then(() => {
							setIsLoading(false);
						})
						.catch((error) => {
							console.error("Error fetching round ranking data:", error);
							setToastMessage({
								text: error.data?.error?.message || error.message || "Error loading ranking data!",
								type: "error",
							});
							setIsLoading(false);
						});
				})
				.catch((importError) => {
					console.error("Error importing ranking module:", importError);
					setToastMessage({
						text: "Failed to load ranking data",
						type: "error",
					});
					setIsLoading(false);
				});
		},
		[dispatch, fansUnitedSDK],
	);

	const getData = useCallback(() => {
		setIsLoading(true);
		dispatch(getRankingRequest(fansUnitedSDK))
			.then(() => {
				setIsLoading(false);
			})
			.catch((error) =>
				setToastMessage({
					text: error.data?.error?.message || "Error!",
					type: "error",
				}),
			);
	}, [dispatch, fansUnitedSDK]);

	const getPersonalRankingData = useCallback(() => {
		setIsLoading(true);
		dispatch(getMyRankingRequest(fansUnitedSDK))
			.then(() => {
				setIsLoading(false);
			})
			.catch((error) =>
				setToastMessage({
					text: error.data?.error?.message || "Error!",
					type: "error",
				}),
			);
	}, [dispatch, fansUnitedSDK]);

	const getWeeklyPersonalRankingData = useCallback(
		(groupId) => {
			if (!user?.id) {
				return;
			}

			setIsLoadingWeeklyRanking(true);
			dispatch(getMyWeeklyRankingRequest(fansUnitedSDK, groupId, user.id))
				.then(() => {
					setIsLoadingWeeklyRanking(false);
				})
				.catch((error) => {
					setToastMessage({
						text: error.data?.error?.message || error.message || "Error loading weekly ranking data!",
						type: "error",
					});
					setIsLoadingWeeklyRanking(false);
				});
		},
		[dispatch, fansUnitedSDK, user],
	);

	// Helper function to get the most recently completed round for leaderboard display
	const getCurrentActiveRound = (groups) => {
		if (!groups || groups.length === 0) {
			return null;
		}

		const now = new Date();
		const todayStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format

		// First, check if we're currently in a multi-day period (current active round)
		const currentGroup = groups.find((group) => {
			if (!group.filters) return false;

			const { fromDate, toDate } = group.filters;
			if (!fromDate || !toDate) return false;

			return todayStr >= fromDate && todayStr <= toDate;
		});

		if (currentGroup) {
			return currentGroup.groupId;
		}

		// If no current group, return the most recent completed group (past group)
		const pastGroups = groups.filter((group) => {
			if (!group.filters || !group.filters.toDate) return false;
			return group.filters.toDate < todayStr;
		});

		if (pastGroups.length > 0) {
			// Sort by toDate descending and return the most recent completed
			return pastGroups.sort((a, b) => b.filters.toDate.localeCompare(a.filters.toDate))[0].groupId;
		}

		// If no past groups, find the next upcoming group (fallback for new seasons)
		const upcomingGroup = groups.find((group) => {
			if (!group.filters || !group.filters.fromDate) return false;
			return group.filters.fromDate > todayStr;
		});

		if (upcomingGroup) {
			return upcomingGroup.groupId;
		}

		// Final fallback to first group
		return groups[0]?.groupId;
	};

	// Handler for ranking type change
	const handleRankingTypeChange = (option) => {
		setRankingType(option.value);
		setDisplayedCount(20); // Reset to show first 20 users

		// If switching to weekly, automatically select the most recently completed round
		if (option.value === "weekly" && groups.length > 0) {
			const activeRoundId = getCurrentActiveRound(groups);
			if (activeRoundId) {
				setSelectedRound(activeRoundId);
			}
		}
	};

	useEffect(() => {
		if (!fansUnitedSDK) return;

		getData();
		getPersonalRankingData();
	}, [fansUnitedSDK, getData, getPersonalRankingData]);

	// Set initial round to most recently completed week when groups are loaded
	useEffect(() => {
		if (groups.length > 0 && !selectedRound) {
			const activeRoundId = getCurrentActiveRound(groups);
			setSelectedRound(activeRoundId || groups[0].groupId);
		}
	}, [groups, selectedRound]);

	// Fetch round ranking when weekly is selected and round changes
	useEffect(() => {
		if (rankingType === "weekly" && selectedRound && fansUnitedSDK) {
			setDisplayedCount(20); // Reset displayed count when switching rounds
			getRoundRankingData(selectedRound);
		}
	}, [rankingType, selectedRound, fansUnitedSDK, getRoundRankingData]);

	// Fetch weekly personal ranking when weekly is selected and round changes
	useEffect(() => {
		if (rankingType === "weekly" && selectedRound && fansUnitedSDK) {
			getWeeklyPersonalRankingData(selectedRound);
		}
	}, [rankingType, selectedRound, fansUnitedSDK, getWeeklyPersonalRankingData]);

	const loadMoreRanking = async () => {
		setIsLoadingMore(true);
		try {
			const nextPage = Math.floor(displayedCount / 20) + 1;
			const filters = { limit: 20, page: nextPage };

			const { getOverallRanking, getRoundRanking } = await import("../../api/rankingService");

			let response;
			if (rankingType === "season") {
				response = await getOverallRanking(fansUnitedSDK, undefined, filters);
			} else {
				response = await getRoundRanking(fansUnitedSDK, selectedRound, filters);
			}

			if (response.data && response.data.length > 0) {
				// Update the Redux store with additional data
				const currentData = rankingType === "season" ? overallRankingList : roundRankingList;
				const updatedData = [...currentData, ...response.data];

				if (rankingType === "season") {
					dispatch({ type: "Ranking/setRanking", payload: updatedData });
				} else {
					dispatch({ type: "Ranking/setRoundRanking", payload: updatedData });
				}

				setDisplayedCount((prev) => prev + 20);
			}
		} catch (error) {
			console.error("Error loading more ranking data:", error);
			setToastMessage({
				text: error.data?.error?.message || error.message || "Error loading more data!",
				type: "error",
			});
		} finally {
			setIsLoadingMore(false);
		}
	};

	// Helper function to format date ranges
	const formatDateRange = (fromDate, toDate) => {
		if (!fromDate || !toDate) return "";
		const from = new Date(fromDate);
		const to = new Date(toDate);
		const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

		const fromDay = from.getDate();
		const toDay = to.getDate();
		const fromMonth = from.getMonth();
		const toMonth = to.getMonth();

		if (fromDate === toDate) {
			return `${fromDay} ${months[fromMonth]}`;
		}

		if (fromMonth === toMonth) {
			return `${fromDay}-${toDay} ${months[fromMonth]}`;
		}

		return `${fromDay} ${months[fromMonth]} - ${toDay} ${months[toMonth]}`;
	};

	// Prepare dropdown options
	const rankingTypeOptions = [
		{ value: "season", label: t("ranking.season") },
		{ value: "weekly", label: t("ranking.weekly") },
	];

	const roundOptions = groups.map((group) => ({
		value: group.groupId,
		label: `${group.label} (${formatDateRange(group.filters?.fromDate, group.filters?.toDate)})`,
	}));

	const RankingHeader = () => (
		<div className={styles.header}>
			<div className={styles.dropdownContainer}>
				<div className={styles.dropdownWrapper}>
					<div className={styles.dropdown}>
						<Dropdown
							options={rankingTypeOptions}
							value={rankingType}
							onChange={handleRankingTypeChange}
							placeholder={t("ranking.selectType")}
						/>
					</div>
				</div>
				{rankingType === "weekly" && (
					<div className={styles.dropdownWrapper}>
						<div className={styles.dropdown}>
							<Dropdown
								options={roundOptions}
								value={selectedRound}
								onChange={(option) => setSelectedRound(option.value)}
								placeholder={t("ranking.selectWeek")}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);

	const UserRanking = () => {
		// Determine which ranking data to use based on ranking type
		const currentRankingList = rankingType === "weekly" ? myWeeklyRankingList : myRankingList;
		const currentUserRanking = currentRankingList?.filter((template) => template.id === TEMPLATE_ID)[0]?.position || "-";
		const currentUserPoints = currentRankingList?.filter((template) => template.id === TEMPLATE_ID)[0]?.points || "-";

		// Show loading state for weekly rankings
		const showLoadingState = rankingType === "weekly" && isLoadingWeeklyRanking;

		return (
			<div className={styles.myPositionSection}>
				<div className={styles.sectionHeader}>
					<h3 className={styles.sectionTitle}>{t("ranking.myPosition")}</h3>
					<div className={styles.sectionLabels}>
						<span className={styles.pointsLabel}>{t("ranking.points")}</span>
						<span className={styles.rankLabel}>{t("ranking.rank")}</span>
					</div>
				</div>
				<div className={styles.userRanking}>
					<div className={styles.userInfo}>
						<img className={styles.avatarIcon} src={user?.avatar ?? avatarIcon} alt={"user-icon"} />
						<span className={styles.playerName}>{printProfileNickname(user.nickname) ?? user.name}</span>
					</div>
					<div className={styles.userStats}>
						<span className={styles.playerPoints}>
							{showLoadingState ? <div className={styles.miniLoader}></div> : currentUserPoints}
						</span>
						<span className={styles.playerRank}>{showLoadingState ? <div className={styles.miniLoader}></div> : currentUserRanking}</span>
					</div>
				</div>
			</div>
		);
	};

	const RankingList = () => {
		const rankingData = rankingType === "season" ? overallRankingList : roundRankingList;
		const displayData = rankingData.slice(0, displayedCount);
		// Show load more button if we have data and we're showing exactly what we have (meaning there might be more)
		const hasMoreData = rankingData.length > 0 && rankingData.length >= displayedCount;

		return (
			<div className={styles.topMembersSection}>
				<div className={styles.sectionHeader}>
					<h3 className={styles.sectionTitle}>{t("ranking.topMembers")}</h3>
					<div className={styles.sectionLabels}>
						<span className={styles.pointsLabel}>{t("ranking.points")}</span>
						<span className={styles.rankLabel}>{t("ranking.rank")}</span>
					</div>
				</div>
				<div className={styles.membersList}>
					{displayData &&
						[...displayData]
							.sort((a, b) => a.position - b.position)
							.map((player) => (
								<div className={styles.memberRow} key={player.profileId}>
									<div className={styles.memberInfo}>
										<img className={styles.avatarIcon} src={player.profileModel?.avatar ?? avatarIcon} alt={"user-icon"} />
										<span className={`${styles.playerName} ${player.position === 1 ? styles.firstPlace : ""}`}>
											{printProfileNickname(player.profileModel?.nickname) ?? player.profileModel?.name}
										</span>
									</div>
									<div className={styles.memberStats}>
										<span className={`${styles.playerPoints} ${player.position === 1 ? styles.firstPlace : ""}`}>
											{player.points}
										</span>
										<span className={`${styles.playerRank} ${player.position === 1 ? styles.firstPlace : ""}`}>
											{player.position}
										</span>
									</div>
								</div>
							))}
				</div>
				{hasMoreData && (
					<div className={styles.loadMoreContainer}>
						<button className={styles.loadMoreButton} onClick={loadMoreRanking} disabled={isLoadingMore}>
							{isLoadingMore ? t("ranking.loading") : t("ranking.loadMore")}
						</button>
					</div>
				)}
			</div>
		);
	};

	const EmptyListCard = () => (
		<div className={styles.emptyListContainer}>
			<img className={styles.emptyListImage} src={noMembersImage} alt={"empty-list-img"} />
			<div className={styles.emptyListTitle}>{t("ranking.noRanking")}</div>
			<div className={styles.emptyListDescription}>{t("ranking.competitionStart")}</div>
		</div>
	);

	const hasRankingData = rankingType === "season" ? overallRankingList.length > 0 : roundRankingList.length > 0;

	return isLoading ? (
		<Loader />
	) : (
		<div className={styles.container}>
			<RankingHeader />
			{hasRankingData ? (
				<>
					<UserRanking />
					<RankingList />
				</>
			) : (
				<EmptyListCard />
			)}
			{showToast && <Toast message={toastMessage} />}
		</div>
	);
};

export default RankingFragment;
